import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { ServicesGrid } from '../components/ServicesGrid';
import { BrochureButton } from '../components/BrochureButton';
import { Check, User, Languages, MapPin, Mail, Phone, MessageCircle, CalendarClock, X } from 'lucide-react';

const expectations = [
  'Het gesprek is vertrouwelijk',
  'De melder houdt zelf de regie: er gebeurt niets zonder instemming',
  'De vertrouwenspersoon denkt mee, geeft advies en helpt bij het kiezen van vervolgstappen',
  'Waar nodig kan de vertrouwenspersoon een melder begeleiden',
];

const choices = [
  { icon: User, text: 'Man of vrouw als vertrouwenspersoon' },
  { icon: Languages, text: 'Gesprek in het Nederlands of Engels' },
  { icon: MapPin, text: 'Online, telefonisch of fysiek in een neutrale ruimte nabij de organisatie' },
];

const contactOptions = [
  { icon: Mail, label: 'Mail' },
  { icon: Phone, label: 'Telefoon' },
  { icon: MessageCircle, label: 'WhatsApp' },
  { icon: CalendarClock, label: 'Spreekuurafspraak' },
];

export function ExterneVertrouwenspersoon() {
  return (
    <>
      <SEO title="Externe Vertrouwenspersoon" description="Wat doet een vertrouwenspersoon? PuntUit biedt onafhankelijke, LVV-gecertificeerde registervertrouwenspersonen die de melder helpen het probleem zelf op te lossen." canonicalUrl="/externe-vertrouwenspersoon" />
      <SchemaMarkup
        type="service"
        pageUrl="https://puntuit.nl/externe-vertrouwenspersoon"
        pageTitle="De vertrouwenspersoon"
        pageDescription="Wat doet een vertrouwenspersoon? PuntUit biedt onafhankelijke, LVV-gecertificeerde registervertrouwenspersonen die de melder helpen het probleem zelf op te lossen."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'De vertrouwenspersoon', url: '/externe-vertrouwenspersoon' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-4xl">
            <p className="text-sm mb-4">De vertrouwenspersoon</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Externe vertrouwenspersoon: wat doet die precies?</h1>
            <p className="text-xl text-primary-100 mb-6">Een vertrouwenspersoon is het aanspreekpunt bij ongewenste omgangsvormen: intimidatie, pesten, agressie en geweld, discriminatie, seksuele intimidatie en integriteit.</p>
            <div className="flex gap-4 flex-wrap items-center">
              <Link to="/contact" className="btn-primary">Plan een kennismakingsgesprek</Link>
              <Link to="/tarieven" className="bg-white text-[#0284c7] px-6 py-3 rounded-lg font-medium">Bekijk tarieven</Link>
              <BrochureButton variant="link-white" />
            </div>
          </div>
        </section>

        {/* Begeleiding, geen overname */}
        <section className="section-padding bg-white">
          <div className="container max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-5 text-gray-700">
                <h2 className="text-3xl font-bold text-gray-900">Begeleiding, geen overname.</h2>
                <p>Het doel van de vertrouwenspersoon is niet om het probleem over te nemen, maar om de melder te helpen het probleem primair zelf op te lossen. Dat begint met luisteren en eindigt met concrete opties en handvatten.</p>
                <p>Twijfel je nog of je deze rol intern wilt beleggen of extern? Lees waarom een <Link to="/interne-vs-externe-vp" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">externe vertrouwenspersoon vrijwel altijd én goedkoper én beter</Link> is dan een interne.</p>
                <h3 className="text-lg font-semibold text-gray-900 pt-2">Wat mensen kunnen verwachten</h3>
                <ul className="space-y-3">
                  {expectations.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#0284c7] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-white">
                <img
                  src="/images/gratis-spreekuur.png"
                  alt="Vertrouwelijk gesprek met de vertrouwenspersoon"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keuze voor de melder */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-6xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Keuze voor de melder.</h2>
            <p className="text-gray-700 mb-10 max-w-2xl">De melder bepaalt zelf met wie, in welke taal en op welke manier het gesprek plaatsvindt.</p>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {choices.map(({ icon: Icon, text }) => (
                <div key={text} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[#0284c7]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#0284c7]" />
                  </div>
                  <p className="text-gray-700">{text}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <p className="text-gray-700 mb-5">Contact opnemen kan via <a href="https://praatuit.nl" target="_blank" rel="noopener noreferrer" className="text-[#0284c7] font-medium underline decoration-[#0284c7]/30">praatuit.nl</a>: mail, telefoon, WhatsApp of door direct een spreekuurafspraak in te plannen.</p>
              <div className="flex flex-wrap gap-3">
                {contactOptions.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm text-gray-700">
                    <Icon className="w-4 h-4 text-[#0284c7]" />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Wat een vertrouwenspersoon niet is */}
        <section className="section-padding bg-white">
          <div className="container max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#E85D56]/10 flex items-center justify-center">
                <X className="w-5 h-5 text-[#E85D56]" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Wat een vertrouwenspersoon niet is.</h2>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">De vertrouwenspersoon is geen coach, mediator, klachtencommissie of HR-adviseur. Vragen die niet binnen de zes genoemde categorieën vallen — intimidatie, pesten, agressie, discriminatie, seksuele intimidatie of integriteitsschendingen — horen niet binnen de werkzaamheden van de vertrouwenspersoon.</p>
          </div>
        </section>

        <ServicesGrid />
      </div>
    </>
  );
}
